/**
 * useFieldTools — fetches and caches the user's field-tools capability
 * once per session. Returns ``{loading, allowed, role, geocoding_configured}``.
 *
 * Used by gated UI to decide whether to render the 🎙️ Record button
 * and the "Find customers near me" entry in TopBarToolsMenu.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

let _cache = null;       // {allowed, role, geocoding_configured, radius_stops_m}
let _inflight = null;    // Promise so we don't double-fetch
const _listeners = new Set();

function _notify() {
  for (const fn of _listeners) {
    try { fn(_cache); } catch { /* noop */ }
  }
}

export function clearFieldToolsCache() {
  _cache = null;
  _inflight = null;
  _notify();
}

async function _load() {
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const r = await api.get("/field-tools/me");
      _cache = r.data || { allowed: false };
    } catch {
      _cache = { allowed: false };
    } finally {
      _inflight = null;
      _notify();
    }
    return _cache;
  })();
  return _inflight;
}

export function useFieldTools() {
  const [state, setState] = useState(() => _cache || { loading: true, allowed: false });

  useEffect(() => {
    let mounted = true;
    const onUpdate = (c) => mounted && setState(c || { loading: false, allowed: false });
    _listeners.add(onUpdate);
    if (_cache) {
      setState(_cache);
    } else {
      _load();
    }
    return () => {
      mounted = false;
      _listeners.delete(onUpdate);
    };
  }, []);

  return {
    loading: state?.loading || false,
    allowed: !!state?.allowed,
    role: state?.role || "",
    geocodingConfigured: !!state?.geocoding_configured,
    radiusStopsM: state?.radius_stops_m || [50, 100, 200, 500, 1000, 5000],
  };
}
